import React, { useMemo, useEffect, useCallback } from 'react';
import { Box, Chip, Grid, Paper, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import PrintIcon from '@mui/icons-material/Print';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import EventBusyIcon from '@mui/icons-material/EventBusy';
import EventIcon from '@mui/icons-material/Event';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { WorkflowPage } from '../../../components';
import { FORM_CONFIGS } from '../../../constants/formConfigurations';
import { TABLE_COLUMNS } from '../../../constants/tableConfigurations';
import { profileLeavesTabStyles } from './ProfileLeavesTab.styles';
import { useAuth, useLeaveRequests, useLeaveTypes, useProfile } from '../../../hooks';

const ProfileLeavesTab = ({ userId = null }) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();

  const targetUserId = userId || user?.id;

  const { leaveRequests, isLoading, handleFormSubmit, fetchData, deleteLeaves } =
    useLeaveRequests(userId);
  const { leaveTypes, fetchData: fetchLeaveTypes } = useLeaveTypes();
  const { profileData, fetchProfile } = useProfile({ 
    skipAutoLoad: !!userId 
  });

  // Fetch profile if userId is provided
  useEffect(() => {
    if (userId) {
      fetchProfile(userId);
    }
  }, [userId, fetchProfile]);

  useEffect(() => {
    if (targetUserId) {
      fetchData();
      fetchLeaveTypes();
    }
  }, [targetUserId]);

  const formFields = useMemo(
    () => {
      const shouldShowBirthdayLeave = () => {
        if (!user?.birthDate) return false;
        
        const today = new Date();
        const currentMonth = today.getMonth();
        const currentYear = today.getFullYear();
        
        const birthDate = new Date(user.birthDate);
        const birthMonth = birthDate.getMonth(); 
        
        const nextMonth = (currentMonth + 1) % 12;
        return currentMonth === birthMonth || nextMonth === birthMonth;
      };

      const isHourlyLeaveType = (leaveTypeId) => {
        if (!leaveTypeId || !Array.isArray(leaveTypes)) return false;
        const id = parseInt(leaveTypeId, 10);
        const lt = leaveTypes.find(x => x.id === id);
        if (!lt) return false;

        const name = lt.leave_type_name?.toLowerCase().trim() || '';
        const days = lt.leave_type_days ?? 0;
        const hours = lt.leave_type_hours ?? 0;

        // Sadece saatlik tanımlanmış izinler
        if (hours > 0 && (!days || days === 0)) return true;
        // İsimden yakalama
        if (
          name.includes('saatlik') ||
          name.includes('hourly') ||
          name.includes(' saat ')
        ) {
          return true;
        }
        return false;
      };

      return FORM_CONFIGS.leaves.fields.map(field => {
        if (field.name === 'leaveType') {
          return {
            ...field,
            options: leaveTypes
              .filter(lt => {
                if (!lt.leave_type_status) return false;
                
                const isBirthdayLeave = lt.leave_type_name?.toLowerCase().includes('doğum') || 
                                      lt.leave_type_name?.toLowerCase().includes('birthday') ||
                                      lt.leave_type_name?.toLowerCase().includes('dogum');
                
                if (isBirthdayLeave) {
                  return shouldShowBirthdayLeave();
                }
                
                return true;
              })
              .map(lt => ({
                value: lt.id,
                labelKey: lt.leave_type_name,
              })),
          };
        }

        if (field.name === 'startDate' || field.name === 'endDate') {
          return {
            ...field,
            showTime: formData => isHourlyLeaveType(formData?.leaveType),
          };
        }

        return field;
      });
    },
    [leaveTypes, user?.birthDate]
  );

  const columns = useMemo(
    () =>
      TABLE_COLUMNS.leaves.map(col =>
        col.field === 'status'
          ? {
              ...col,
              render: (value, row) => {
                if (value === 'REJECTED') {
                  return (
                    <Box component="span" sx={{ display: 'inline-block', pointerEvents: 'none' }}>
                      <Chip
                        label={t('leaves.status.rejected')}
                        color="error"
                        size="small"
                        sx={profileLeavesTabStyles.statusChip('REJECTED')}
                      />
                    </Box>
                  );
                }
                if (value === 'APPROVED') {
                  return (
                    <Box component="span" sx={{ display: 'inline-block', pointerEvents: 'none' }}>
                      <Chip
                        label={t('leaves.status.approved')}
                        color="success"
                        size="small"
                        sx={profileLeavesTabStyles.statusChip('APPROVED')}
                      />
                    </Box>
                  );
                }
                if (row.is_group_manager_approved === false) {
                  return (
                    <Box component="span" sx={{ display: 'inline-block', pointerEvents: 'none' }}>
                      <Chip
                        label={t('leaves.status.groupRejected')}
                        color="error"
                        size="small"
                        sx={profileLeavesTabStyles.statusChip('GROUP_REJECTED')}
                      />
                    </Box>
                  );
                }
                if (row.is_team_manager_approved === false) {
                  return (
                    <Box component="span" sx={{ display: 'inline-block', pointerEvents: 'none' }}>
                      <Chip
                        label={t('leaves.status.teamRejected')}
                        color="error"
                        size="small"
                        sx={profileLeavesTabStyles.statusChip('TEAM_REJECTED')}
                      />
                    </Box>
                  );
                }
                if (row.is_group_manager_approved === true) {
                  return (
                    <Box component="span" sx={{ display: 'inline-block', pointerEvents: 'none' }}>
                      <Chip
                        label={t('leaves.status.groupManagerApproved')}
                        color="info"
                        size="small"
                        sx={profileLeavesTabStyles.statusChip('GROUP_MANAGER_APPROVED')}
                      />
                    </Box>
                  );
                }
                if (row.is_team_manager_approved === true) {
                  return (
                    <Box component="span" sx={{ display: 'inline-block', pointerEvents: 'none' }}>
                      <Chip
                        label={t('leaves.status.teamManagerApproved')}
                        color="info"
                        size="small"
                        sx={profileLeavesTabStyles.statusChip('TEAM_MANAGER_APPROVED')}
                      />
                    </Box>
                  );
                }
                return (
                  <Box component="span" sx={{ display: 'inline-block', pointerEvents: 'none' }}>
                    <Chip
                      label={t(`leaves.tabs.${value}`)}
                      sx={profileLeavesTabStyles.statusChip(value)}
                      size="small"
                      clickable={false}
                      component="span"
                      tabIndex={-1}
                      onKeyDown={e => e.preventDefault()}
                      onClick={e => e.preventDefault()}
                    />
                  </Box>
                );
              },
            }
          : col
      ),
    [t]
  );

  const handleAddLeave = useCallback(async (data, isEditMode, currentEditData) => {
    try {
      // If editing, pass the entity ID
      const entityId = isEditMode && currentEditData?.id ? currentEditData.id : null;
      const result = await handleFormSubmit(data, entityId);
      return result;
    } catch (error) {
      return { success: false, error };
    }
  }, [handleFormSubmit]);

  const handleDeleteLeave = useCallback(async (row) => {
    try {
      // Extract ID from row object
      const leaveId = row?.id || row;
      if (!leaveId) {
        throw new Error('Invalid leave ID');
      }
      const result = await deleteLeaves(leaveId);
      return result;
    } catch (error) {
      return { success: false, error };
    }
  }, [deleteLeaves]);

  const transformEditData = useCallback((leaveRequest) => {
    if (!leaveRequest) return {};
    
    // Use leaveTypeId from the data, or find it from leaveTypes as fallback
    const leaveTypeId = leaveRequest.leaveTypeId || leaveTypes.find(
      lt => lt.leave_type_name === leaveRequest.leaveTypeName
    )?.id;

    return {
      startDate: leaveRequest.startDate ? new Date(leaveRequest.startDate) : null,
      endDate: leaveRequest.endDate ? new Date(leaveRequest.endDate) : null,
      leaveType: leaveTypeId || null,
      leave_request_description: leaveRequest.description || '',
    };
  }, [leaveTypes]);

  const rowActions = useCallback((row) => {
    const actions = [];

    // Pending ise düzenleme/silme
    if (row.status === 'PENDING') {
      actions.push(
        {
          type: 'edit',
          label: 'table.actions.edit',
          color: 'primary',
          icon: <EditIcon fontSize="small" />,
        },
        {
          type: 'delete',
          label: 'table.actions.delete',
          color: 'error',
          icon: <DeleteIcon fontSize="small" />,
        }
      );
    }

    // Onaylanmış izinleri yazdırabilsin
    if (row.status === 'APPROVED') {
      actions.push({
        type: 'print',
        label: 'leaves.actions.print',
        color: 'primary',
        icon: <PrintIcon fontSize="small" />,
      });
    }

    return actions;
  }, [t]);

  const handleRowAction = useCallback((actionType, row) => {
    if (actionType === 'print' && row?.id) {
      navigate(`/applications/leave-requests/print/${row.id}?from=profile`);
      return true;
    }
    return false;
  }, [navigate]);

  const statusCounts = useMemo(() => {
    if (!Array.isArray(leaveRequests))
      return {
        all: 0,
        PENDING: 0,
        TEAM_MANAGER_APPROVED: 0,
        GROUP_MANAGER_APPROVED: 0,
        APPROVED: 0,
        REJECTED: 0,
      };
    const counts = {
      all: leaveRequests.length,
      PENDING: 0,
      TEAM_MANAGER_APPROVED: 0,
      GROUP_MANAGER_APPROVED: 0,
      APPROVED: 0,
      REJECTED: 0,
    };
    leaveRequests.forEach(l => {
      if (counts.hasOwnProperty(l.status)) {
        counts[l.status]++;
      }
    });
    return counts;
  }, [leaveRequests]);

  const leaveStats = useMemo(() => {
    const calculateAnnualLeaveEntitlement = (workStartDate) => {
      if (!workStartDate) return 0;
      
      const startDate = new Date(workStartDate);
      const today = new Date();
      
      const yearsOfService = today.getFullYear() - startDate.getFullYear();
      const monthDiff = today.getMonth() - startDate.getMonth();
      const dayDiff = today.getDate() - startDate.getDate();
      
      let actualYears = yearsOfService;
      if (monthDiff < 0 || (monthDiff === 0 && dayDiff < 0)) {
        actualYears = yearsOfService - 1;
      }
      
      if (actualYears < 1) {
        return 0; 
      } else if (actualYears < 5) {
        return 112; 
      } else if (actualYears < 15) {
        return 160;
      } else {
        return 208;
      }
    };

    const parseDurationToHours = (durationString) => {
      if (!durationString || typeof durationString !== 'string') return 0;
      
      let totalHours = 0;
      const parts = durationString.match(/(-?\d+)\s*(gün|saat|dakika)/g);
      
      if (!parts) return 0;
      
      parts.forEach(part => {
        const match = part.match(/(-?\d+)\s*(gün|saat|dakika)/);
        if (match) {
          const value = parseInt(match[1], 10);
          const unit = match[2];
          if (unit === 'gün') {
            totalHours += value * 8; // 1 day = 8 hours
          } else if (unit === 'saat') {
            totalHours += value;
          } else if (unit === 'dakika') {
            totalHours += value / 60;
          }
        }
      });
      
      return totalHours;
    };

    const workStartDate = profileData?.startDate;
    const annualLeaveEntitlement = calculateAnnualLeaveEntitlement(workStartDate);
    
    const remainingLeaveHours = profileData?.annualLeaveBalance ?? 0;
    
    const usedLeaveHours = annualLeaveEntitlement - remainingLeaveHours;
    
    // Calculate totals by leave type
    const leaveTypeTotals = {};
    if (Array.isArray(leaveRequests)) {
      leaveRequests.forEach(leave => {
        // Only count approved leaves
        if (leave.status === 'APPROVED') {
          const leaveTypeName = leave.leaveTypeName || '';
          const durationHours = parseDurationToHours(leave.duration);
          
          if (!leaveTypeTotals[leaveTypeName]) {
            leaveTypeTotals[leaveTypeName] = 0;
          }
          leaveTypeTotals[leaveTypeName] += durationHours;
        }
      });
    }

    // Categorize leave types - check for exact matches and partial matches
    const hourlyLeaveTotal = Object.keys(leaveTypeTotals).reduce((sum, typeName) => {
      const lowerName = typeName.toLowerCase().trim();
      // Check for hourly leave patterns: "saatlik izin", "2 saatlik izin", "hourly", "saat" etc.
      if (lowerName.includes('saatlik') || 
          lowerName.includes('hourly') || 
          (lowerName.includes('saat') && (lowerName.includes('izin') || lowerName.includes('leave'))) ||
          /^\d+\s*saat/.test(lowerName)) { // Matches "2 saatlik izin", "3 saat izin" etc.
        return sum + leaveTypeTotals[typeName];
      }
      return sum;
    }, 0);

    const excuseLeaveTotal = Object.keys(leaveTypeTotals).reduce((sum, typeName) => {
      const lowerName = typeName.toLowerCase().trim();
      // Check for excuse leave patterns: "mazeret izni", "mazaret izni", "excuse" etc.
      if (lowerName.includes('mazeret') || 
          lowerName.includes('mazaret') || 
          lowerName.includes('excuse')) {
        return sum + leaveTypeTotals[typeName];
      }
      return sum;
    }, 0);

    const reportLeaveTotal = Object.keys(leaveTypeTotals).reduce((sum, typeName) => {
      const lowerName = typeName.toLowerCase().trim();
      // Check for report leave patterns: "rapor izni", "rapor", "report", "sick" etc.
      if (lowerName.includes('rapor') || 
          lowerName.includes('report') || 
          lowerName.includes('sick')) {
        return sum + leaveTypeTotals[typeName];
      }
      return sum;
    }, 0);
    
    const formatHoursToDaysAndHours = (hours) => {
      if (hours === null || hours === undefined) return '0 gün 0 saat 0 dakika';
      
      const totalHours = parseFloat(hours);
      const isNegative = totalHours < 0;
      const absHours = Math.abs(totalHours);
      
      const days = Math.floor(absHours / 8);
      const remainingHoursDecimal = absHours % 8;
      const hoursInt = Math.floor(remainingHoursDecimal);
      const minutes = Math.round((remainingHoursDecimal - hoursInt) * 60);
      
      const sign = isNegative ? '-' : '';
      const daysText = days === 1 ? t('profile.fields.day', 'gün') : t('profile.fields.days', 'gün');
      const hoursText = hoursInt === 1 ? t('profile.fields.hour', 'saat') : t('profile.fields.hours', 'saat');
      const minutesText = minutes === 1 ? t('profile.fields.minute', 'dakika') : t('profile.fields.minutes', 'dakika');
      
      const parts = [];
      if (days > 0) parts.push(`${sign}${days} ${daysText}`);
      if (hoursInt > 0) parts.push(`${hoursInt} ${hoursText}`);
      if (minutes > 0) parts.push(`${minutes} ${minutesText}`);
      
      if (parts.length === 0) {
        return '0 gün 0 saat 0 dakika';
      }
      
      return parts.join(' ');
    };

    const formatHoursToDaysOnly = (hours) => {
      if (hours === null || hours === undefined) return '0 gün';
      
      const totalHours = parseFloat(hours);
      const isNegative = totalHours < 0;
      const absHours = Math.abs(totalHours);
      
      const days = Math.round(absHours / 8);
      
      const sign = isNegative ? '-' : '';
      const daysText = days === 1 ? t('profile.fields.day', 'gün') : t('profile.fields.days', 'gün');
      
      return `${sign}${days} ${daysText}`;
    };

    const totalLeavesUsed = profileData?.totalLeavesUsed ?? 0;
    const userAnnualLeaveBalance = profileData?.userAnnualLeaveBalance ?? 0;
    return {
      annualLeaveEntitlement,
      usedLeaveHours,
      remainingLeaveHours,
      totalLeavesUsed,
      userAnnualLeaveBalance,
      formatHoursToDaysAndHours,
      formatHoursToDaysOnly,
      hourlyLeaveTotal,
      excuseLeaveTotal,
      reportLeaveTotal,
    };
  }, [profileData?.startDate, profileData?.annualLeaveBalance, profileData?.totalLeavesUsed, profileData?.userAnnualLeaveBalance, leaveRequests, t]);

  const pageConfig = useMemo(() => ({
    columns,
    filterConfigs: [],
    searchConfig: { fields: ['reason'], placeholder: 'İzin arama...' },
    formConfig: { 
      fields: formFields,
      title: FORM_CONFIGS.leaves.title
    },
  }), [columns, formFields]);

  return (
    <Box>
      <Grid container spacing={1.5} sx={{ mb: 2.5 }}>
        <Grid item xs={12} sm={6} md={4}>
          <Paper
            elevation={2}
            sx={{
              p: 1.5,
              background: 'linear-gradient(135deg, #4caf50 0%, #388e3c 100%)',
              color: 'white',
              borderRadius: 1.5,
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              gap: 1.5,
              minHeight: 80,
              transition: 'transform 0.2s, box-shadow 0.2s',
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: 4,
              },
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                minWidth: 48,
                height: 48,
                borderRadius: 1,
                backgroundColor: 'rgba(255, 255, 255, 0.2)',
              }}
            >
              <EventAvailableIcon sx={{ fontSize: 24 }} />
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                variant="caption"
                sx={{
                  display: 'block',
                  opacity: 0.95,
                  mb: 0.25,
                  fontWeight: 500,
                  fontSize: '0.7rem',
                  lineHeight: 1.2,
                }}
              >
                {t('profile.fields.annualLeaveBalance2', 'Yıllık İzin Bakiyesi')}
              </Typography>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  fontSize: '1rem',
                  lineHeight: 1.2,
                  wordBreak: 'break-word',
                }}
              >
                {leaveStats.formatHoursToDaysOnly(leaveStats.userAnnualLeaveBalance)}
              </Typography>
            </Box>
          </Paper>
        </Grid>

        {/* Used Leave */}
        <Grid item xs={12} sm={6} md={4}>
          <Paper
            elevation={2}
            sx={{
              p: 1.5,
              background: 'linear-gradient(135deg, #ff9800 0%, #f57c00 100%)',
              color: 'white',
              borderRadius: 1.5,
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              gap: 1.5,
              minHeight: 80,
              transition: 'transform 0.2s, box-shadow 0.2s',
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: 4,
              },
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                minWidth: 48,
                height: 48,
                borderRadius: 1,
                backgroundColor: 'rgba(255, 255, 255, 0.2)',
              }}
            >
              <EventBusyIcon sx={{ fontSize: 24 }} />
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                variant="caption"
                sx={{
                  display: 'block',
                  opacity: 0.95,
                  mb: 0.25,
                  fontWeight: 500,
                  fontSize: '0.7rem',
                  lineHeight: 1.2,
                }}
              >
                {t('profile.leaves.usedLeave', 'Kullanılan İzin')}
              </Typography>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  fontSize: '1rem',
                  lineHeight: 1.2,
                  wordBreak: 'break-word',
                }}
              >
                {leaveStats.formatHoursToDaysOnly(leaveStats.totalLeavesUsed)}
              </Typography>
            </Box>
          </Paper>
        </Grid>

        {/* Remaining Leave */}
        <Grid item xs={12} sm={6} md={4}>
          <Paper
            elevation={2}
            sx={{
              p: 1.5,
              background: leaveStats.remainingLeaveHours < 0
                ? 'linear-gradient(135deg, #f44336 0%, #d32f2f 100%)'
                : 'linear-gradient(135deg, #2196f3 0%, #1976d2 100%)',
              color: 'white',
              borderRadius: 1.5,
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              gap: 1.5,
              minHeight: 80,
              transition: 'transform 0.2s, box-shadow 0.2s',
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: 4,
              },
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                minWidth: 48,
                height: 48,
                borderRadius: 1,
                backgroundColor: 'rgba(255, 255, 255, 0.2)',
              }}
            >
              <EventIcon sx={{ fontSize: 24 }} />
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                variant="caption"
                sx={{
                  display: 'block',
                  opacity: 0.95,
                  mb: 0.25,
                  fontWeight: 500,
                  fontSize: '0.7rem',
                  lineHeight: 1.2,
                }}
              >
                {t('profile.leaves.remainingLeave', 'Kalan İzin Hakkı')}
              </Typography>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  fontSize: '1rem',
                  lineHeight: 1.2,
                  wordBreak: 'break-word',
                }}
              >
                {leaveStats.formatHoursToDaysOnly(leaveStats.userAnnualLeaveBalance-leaveStats.totalLeavesUsed)}
              </Typography>
            </Box>
          </Paper>
        </Grid>

        {/* Hourly Leave Total */}
        <Grid item xs={12} sm={6} md={4}>
          <Paper
            elevation={2}
            sx={{
              p: 1.5,
              background: 'linear-gradient(135deg, #2196f3 0%, #1976d2 100%)',
              color: 'white',
              borderRadius: 1.5,
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              gap: 1.5,
              minHeight: 80,
              transition: 'transform 0.2s, box-shadow 0.2s',
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: 4,
              },
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                minWidth: 48,
                height: 48,
                borderRadius: 1,
                backgroundColor: 'rgba(255, 255, 255, 0.2)',
              }}
            >
              <EventIcon sx={{ fontSize: 24 }} />
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                variant="caption"
                sx={{
                  display: 'block',
                  opacity: 0.95,
                  mb: 0.25,
                  fontWeight: 500,
                  fontSize: '0.7rem',
                  lineHeight: 1.2,
                }}
              >
                {t('profile.leaves.hourlyLeaveTotal', 'Saatlik İzin Toplamı')}
              </Typography>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  fontSize: '1rem',
                  lineHeight: 1.2,
                  wordBreak: 'break-word',
                }}
              >
                {leaveStats.formatHoursToDaysAndHours(leaveStats.hourlyLeaveTotal)}
              </Typography>
            </Box>
          </Paper>
        </Grid>

        {/* Excuse Leave Total */}
        <Grid item xs={12} sm={4}>
          <Paper
            elevation={2}
            sx={{
              p: 1.5,
              background: 'linear-gradient(135deg, #9c27b0 0%, #7b1fa2 100%)',
              color: 'white',
              borderRadius: 1.5,
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              gap: 1.5,
              minHeight: 80,
              transition: 'transform 0.2s, box-shadow 0.2s',
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: 4,
              },
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                minWidth: 48,
                height: 48,
                borderRadius: 1,
                backgroundColor: 'rgba(255, 255, 255, 0.2)',
              }}
            >
              <EventIcon sx={{ fontSize: 24 }} />
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                variant="caption"
                sx={{
                  display: 'block',
                  opacity: 0.95,
                  mb: 0.25,
                  fontWeight: 500,
                  fontSize: '0.7rem',
                  lineHeight: 1.2,
                }}
              >
                {t('profile.leaves.excuseLeaveTotal', 'Mazeret İzin Toplamı')}
              </Typography>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  fontSize: '1rem',
                  lineHeight: 1.2,
                  wordBreak: 'break-word',
                }}
              >
                {leaveStats.formatHoursToDaysOnly(leaveStats.excuseLeaveTotal)}
              </Typography>
            </Box>
          </Paper>
        </Grid>

        {/* Report Leave Total */}
        <Grid item xs={12} sm={6} md={4}>
          <Paper
            elevation={2}
            sx={{
              p: 1.5,
              background: 'linear-gradient(135deg, #00bcd4 0%, #0097a7 100%)',
              color: 'white',
              borderRadius: 1.5,
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              gap: 1.5,
              minHeight: 80,
              transition: 'transform 0.2s, box-shadow 0.2s',
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: 4,
              },
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                minWidth: 48,
                height: 48,
                borderRadius: 1,
                backgroundColor: 'rgba(255, 255, 255, 0.2)',
              }}
            >
              <EventIcon sx={{ fontSize: 24 }} />
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                variant="caption"
                sx={{
                  display: 'block',
                  opacity: 0.95,
                  mb: 0.25,
                  fontWeight: 500,
                  fontSize: '0.7rem',
                  lineHeight: 1.2,
                }}
              >
                {t('profile.leaves.reportLeaveTotal', 'Rapor Toplamı')}
              </Typography>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  fontSize: '1rem',
                  lineHeight: 1.2,
                  wordBreak: 'break-word',
                }}
              >
                {leaveStats.formatHoursToDaysOnly(leaveStats.reportLeaveTotal)}
              </Typography>
            </Box>
          </Paper>
        </Grid>
      </Grid>

      {/* Leave Requests Table */}
    <WorkflowPage
      entityType="leaves"
      data={leaveRequests}
      loading={isLoading}
      columns={pageConfig.columns}
      formFields={pageConfig.formConfig.fields}
      filterConfigs={pageConfig.filterConfigs}
      searchConfig={pageConfig.searchConfig}
      formConfig={pageConfig.formConfig}
      statusCounts={statusCounts}
      onFormSubmit={handleAddLeave}
      onApprove={null}
      onReject={null}
      rowActions={rowActions}
      onRowAction={handleRowAction}
      onDelete={handleDeleteLeave}
      transformEditData={transformEditData}
      canAdd={true}
      addButtonText={t('leaves.actions.add')}
      addButtonIcon={<AddIcon />}
      currentPage={t('profile.tabs.leaves')}
      showViewGuard={false}
    />
    </Box>
  );
};

export default ProfileLeavesTab;
